import React, { useState, useEffect } from "react";
import { Droplets, Award, Leaf, Coffee, Flower2 } from "lucide-react";

// Product images from user uploads
const POG_RTD = "https://customer-assets.emergentagent.com/job_ty-core/artifacts/3chv92v3_Can%20K%20mockup.jpg";
const POG_BOTTLE = "https://customer-assets.emergentagent.com/job_ty-core/artifacts/tcp9sphy_Pog%20Front%20Label%204.26%20w%20bottle.png";
const MADURO = "https://customer-assets.emergentagent.com/job_ty-core/artifacts/mww36b3g_Maduro%2010.16.jpg";
// Ojo de Dios Mezcal
const OJO_MEZCAL = "https://customer-assets.emergentagent.com/job_ty-core/artifacts/b8m9p7dv_Ojo.png";

const Products = () => {
  const [activeTab, setActiveTab] = useState("all");

  // SEO - Dynamic page title
  useEffect(() => {
    document.title = "Shop POG Vodka, Maduro Tequila & Ojo de Dios Mezcal | 8eight8 Beverage Co";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Buy premium flavored vodka, dark tequila, and artisanal mezcal. POG Vodka (passion fruit orange guava), Maduro Reposado Oscuro tequila, and Ojo de Dios mezcal from Oaxaca.');
    }
  }, []);

  const pogProducts = [
    {
      id: "pog-750ml",
      name: "POG Vodka 750ml",
      subtitle: "Premium Flavored Vodka",
      description: "Our flagship bottle. 35% ALC/VOL (70 Proof). Natural flavors infused with the iconic POG tropical taste.",
      image: POG_BOTTLE,
      specs: ["750ml", "35% ALC/VOL", "70 Proof"],
      features: ["Natural Flavors", "Meadow Gold Partnership", "Premium Distilled"],
    },
    {
      id: "pog-rtd",
      name: "POG Ready to Drink 12oz",
      subtitle: "Ready-to-Drink Cocktail",
      description: "Perfectly mixed and ready to enjoy. 12 FL OZ can at 5% ALC/VOL. The taste of Hawaii in every sip.",
      image: POG_RTD,
      specs: ["12 FL OZ", "5% ALC/VOL", "355ml"],
      features: ["Ready to Drink", "Perfectly Mixed", "Chill & Serve"],
    },
  ];

  const maduroProduct = {
    id: "maduro-reposado",
    name: "Maduro Reposado Oscuro",
    subtitle: "Black Tequila",
    description: "A revolutionary tequila aged in charred oak barrels, creating a dark hue with rich vanilla, caramel, and smoky characters. Specifically crafted for the cigar aficionado.",
    image: MADURO,
    specs: ["750ml", "40% ALC/VOL", "Charred Oak Aged"],
    features: ["Reposado Oscuro", "Cigar Pairing", "Sean Williams Collaboration"],
    story: "Maduro—meaning ripe and mature in Spanish—reflects the bold essence of the cigar world. Oscura adds the element of darkness and mystique. Together they create a tequila unlike any other.",
  };

  const ojoProducts = [
    {
      id: "ojo-joven",
      name: "Ojo de Dios Joven",
      subtitle: "Mezcal Artesanal",
      description: "100% Maguey Espadin. Crafted in Oaxaca with sustainably grown agave hearts cooked in an underground oven for up to 10 days, double distilled in small batches.",
      image: OJO_MEZCAL,
      specs: ["750ml", "42% ALC/VOL", "100% Espadin"],
      features: ["Multi-Award Winning", "Perfect House Pour", "Smoky Margarita"],
      color: "#00CED1",
    },
    {
      id: "ojo-cafe",
      name: "Ojo de Dios Cafe",
      subtitle: "Coffee Mezcal",
      description: "A world's first: Joven Mezcal blended with Arabica coffee grown and roasted by the Mejia Baustista family at 4,600 ft above sea level in Oaxaca.",
      image: OJO_MEZCAL,
      specs: ["750ml", "35% ALC/VOL", "Natural Coffee"],
      features: ["World's First", "Espresso Martini", "Coffee Negroni"],
      color: "#8B4513",
    },
    {
      id: "ojo-hibiscus",
      name: "Ojo de Dios Hibiscus",
      subtitle: "Hibiscus Mezcal",
      description: "A world's first: Joven Mezcal blended with hibiscus flowers grown in the mountains of Sierra Madre Oriental, Oaxaca. Mexico's national flower in a bottle.",
      image: OJO_MEZCAL,
      specs: ["750ml", "35% ALC/VOL", "Natural Hibiscus"],
      features: ["World's First", "Summer Cocktails", "Mexican Spritz"],
      color: "#FF1493",
    },
  ];

  const filteredProducts = activeTab === "all" 
    ? { pog: pogProducts, maduro: maduroProduct, ojo: ojoProducts }
    : activeTab === "pog" 
    ? { pog: pogProducts }
    : activeTab === "maduro"
    ? { maduro: maduroProduct }
    : { ojo: ojoProducts };

  return (
    <div className="bg-[#050505] pt-20" data-testid="products-page">
      {/* Hero */}
      <section className="py-24 sm:py-32">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto">
            <p className="font-subheading text-xs tracking-[0.3em] uppercase text-[#C8A876] mb-4">
              The Collection
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter text-white mb-6">
              Our Products
            </h1>
            <p className="text-[#A3A3A3] text-lg">
              From the tropical essence of POG Vodka to the dark sophistication of Maduro Tequila 
              and the bold innovation of Ojo de Dios Mezcal.
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mt-12" data-testid="product-filters">
            {[
              { id: "all", label: "All Products" },
              { id: "pog", label: "POG Vodka" },
              { id: "maduro", label: "Maduro Tequila" },
              { id: "ojo", label: "Ojo de Dios" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 text-sm tracking-wide transition-all ${
                  activeTab === tab.id
                    ? "bg-[#C8A876] text-black"
                    : "border border-white/20 text-white hover:bg-white/5"
                }`}
                data-testid={`filter-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="pb-24 sm:pb-32" aria-label="Premium Spirits Products">
        <div className="container-main">
          {/* POG Vodka Section - SEO Optimized */}
          {filteredProducts.pog && (
            <article className="mb-24" data-testid="pog-section" itemScope itemType="https://schema.org/Product">
              <div className="mb-12">
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight mb-4" itemProp="name">
                  <span className="gradient-text-pog">POG Vodka - Best Tropical Flavored Vodka</span>
                </h2>
                <p className="text-[#A3A3A3] max-w-2xl" itemProp="description">
                  The best passion fruit orange guava flavored vodka. Hawaii's beloved #7 ranked tropical juice, 
                  now in premium vodka form. Made in partnership with Meadow Gold for authentic POG flavor.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {pogProducts.map((product) => (
                  <div
                    key={product.id}
                    className="group card-dark p-6"
                    data-testid={`product-card-${product.id}`}
                  >
                    <div className="relative aspect-square bg-[#0A0A0A] mb-6 flex items-center justify-center overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-[80%] h-[80%] object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                      {product.id === 'pog-750ml' && (
                        <div className="absolute inset-0 pointer-events-none" style={{
                          boxShadow: 'inset 0 0 60px 40px #0A0A0A'
                        }} />
                      )}
                    </div>
                    <div>
                      <p className="text-[#FF6B35] text-xs tracking-[0.2em] uppercase mb-2">
                        {product.subtitle}
                      </p>
                      <h3 className="font-heading text-2xl text-white mb-2">
                        {product.name}
                      </h3>
                      <p className="text-[#A3A3A3] text-sm mb-4">
                        {product.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {product.specs.map((spec) => (
                          <span
                            key={spec}
                            className="text-xs px-3 py-1 bg-[#1A1025] border border-[#3B1F54] text-[#FF6B35]"
                          >
                            {spec}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-end pt-4 border-t border-white/5">
                        <div className="flex gap-2">
                          {product.features.slice(0, 2).map((feature) => (
                            <span key={feature} className="text-[#737373] text-xs flex items-center gap-1">
                              <Droplets size={12} className="text-[#FF6B35]" />
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          )}

          {/* Maduro Tequila Section - SEO Optimized */}
          {filteredProducts.maduro && (
            <article className="mb-24" data-testid="maduro-section" itemScope itemType="https://schema.org/Product">
              <div className="mb-12">
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight mb-4" itemProp="name">
                  <span className="gradient-text-maduro">Maduro Tequila - Premium Dark Aged Tequila</span>
                </h2>
                <p className="text-[#A3A3A3] max-w-2xl" itemProp="description">
                  The best dark tequila for cigar lovers. Revolutionary black reposado oscuro tequila aged in charred oak barrels. 
                  Co-created with Sean Williams, Master Blender for Cohiba Cigars.
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div 
                  className="relative aspect-square bg-[#0A0A0A] flex items-center justify-center overflow-hidden border border-white/5"
                  data-testid={`product-card-${maduroProduct.id}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C8A876]/10 to-transparent" />
                  <img
                    src={maduroProduct.image}
                    alt={maduroProduct.name}
                    className="w-3/4 h-3/4 object-contain glow-amber"
                  />
                </div>
                <div>
                  <p className="text-[#C8A876] text-xs tracking-[0.2em] uppercase mb-4">
                    {maduroProduct.subtitle}
                  </p>
                  <h3 className="font-heading text-4xl sm:text-5xl text-white mb-6">
                    {maduroProduct.name}
                  </h3>
                  <p className="text-[#A3A3A3] text-lg mb-6 leading-relaxed">
                    {maduroProduct.description}
                  </p>
                  <p className="text-[#737373] italic mb-8 border-l-2 border-[#C8A876] pl-4">
                    "{maduroProduct.story}"
                  </p>
                  
                  <div className="flex flex-wrap gap-3 mb-8">
                    {maduroProduct.specs.map((spec) => (
                      <span
                        key={spec}
                        className="text-sm px-4 py-2 bg-[#1A1A1A] border border-[#333333] text-[#C8A876]"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {maduroProduct.features.map((feature, i) => {
                      const icons = [
                        <Droplets key="icon" size={18} className="text-[#C8A876]" />,
                        <Leaf key="icon" size={18} className="text-[#C8A876]" />,
                        <Award key="icon" size={18} className="text-[#C8A876]" />
                      ];
                      return (
                        <div key={feature} className="text-center">
                          <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center border border-[#C8A876]/30">
                            {icons[i]}
                          </div>
                          <span className="text-[#A3A3A3] text-xs">{feature}</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-8">
                    <span className="text-[#737373] text-sm">
                      50% owned by Sean Williams
                    </span>
                  </div>
                </div>
              </div>
            </article>
          )}

          {/* Ojo de Dios Mezcal Section - SEO Optimized */}
          {filteredProducts.ojo && (
            <article data-testid="ojo-section" itemScope itemType="https://schema.org/Product">
              <div className="mb-12">
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight mb-4" itemProp="name">
                  <span className="bg-gradient-to-r from-[#00CED1] to-[#FFD700] bg-clip-text text-transparent">Ojo de Dios - Award-Winning Artisanal Mezcal</span>
                </h2>
                <p className="text-[#A3A3A3] max-w-2xl" itemProp="description">
                  Premium artisanal mezcal from Oaxaca, Mexico. Features the world's first coffee mezcal and hibiscus mezcal. 
                  Crafted by Master Blender Cynthia Villalobos and her all-female team. Bold, innovative, award-winning.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {ojoProducts.map((product) => (
                  <div
                    key={product.id}
                    className="group card-dark p-6"
                    data-testid={`product-card-${product.id}`}
                  >
                    <div 
                      className="relative aspect-square mb-6 flex items-center justify-center overflow-hidden"
                      style={{ background: `linear-gradient(135deg, ${product.color}15, transparent)` }}
                    >
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-3/4 h-3/4 object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div>
                      <p className="text-xs tracking-[0.2em] uppercase mb-2" style={{ color: product.color }}>
                        {product.subtitle}
                      </p>
                      <h3 className="font-heading text-2xl text-white mb-2">
                        {product.name}
                      </h3>
                      <p className="text-[#A3A3A3] text-sm mb-4">
                        {product.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {product.specs.map((spec) => (
                          <span
                            key={spec}
                            className="text-xs px-3 py-1 border"
                            style={{ borderColor: `${product.color}40`, color: product.color, backgroundColor: `${product.color}10` }}
                          >
                            {spec}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-end pt-4 border-t border-white/5">
                        <div className="flex items-center gap-1 text-xs" style={{ color: product.color }}>
                          {product.id === "ojo-cafe" && <Coffee size={14} />}
                          {product.id === "ojo-hibiscus" && <Flower2 size={14} />}
                          {product.id === "ojo-joven" && <Award size={14} />}
                          <span>{product.features[0]}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          )}
        </div>
      </section>

      {/* Tasting Notes */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="tasting-notes-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-white mb-4">
              Tasting Notes
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 border border-[#3B1F54] bg-[#1A1025]/50">
              <h3 className="font-heading text-2xl text-[#FF6B35] mb-4">POG Vodka</h3>
              <div className="space-y-4 text-[#A3A3A3]">
                <p><span className="text-white font-medium">Nose:</span> Bright tropical fruits, fresh passion fruit, sweet orange zest</p>
                <p><span className="text-white font-medium">Palate:</span> Smooth, tropical sweetness with guava undertones</p>
                <p><span className="text-white font-medium">Finish:</span> Clean, refreshing, citrus-forward</p>
              </div>
            </div>
            <div className="p-8 border border-[#333333] bg-[#1A1A1A]/50">
              <h3 className="font-heading text-2xl text-[#C8A876] mb-4">Maduro Tequila</h3>
              <div className="space-y-4 text-[#A3A3A3]">
                <p><span className="text-white font-medium">Nose:</span> Rich vanilla, caramel, subtle oak char</p>
                <p><span className="text-white font-medium">Palate:</span> Deep smoky complexity, dark chocolate, agave sweetness</p>
                <p><span className="text-white font-medium">Finish:</span> Long, warm, perfect with a fine cigar</p>
              </div>
            </div>
            <div className="p-8 border border-[#00CED1]/30 bg-[#00CED1]/5">
              <h3 className="font-heading text-2xl text-[#00CED1] mb-4">Ojo de Dios Joven</h3>
              <div className="space-y-4 text-[#A3A3A3]">
                <p><span className="text-white font-medium">Nose:</span> Earthy smoke, roasted agave, mineral notes</p>
                <p><span className="text-white font-medium">Palate:</span> Balanced smoke, approachable, complex</p>
                <p><span className="text-white font-medium">Finish:</span> Clean smoke, perfect for cocktails</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
